import styled from "styled-components";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { SelectedItem, HomeworkSchedule } from "shared/types/Report";
import { useState } from "react";
import Modal from "shared/components/Modal";

interface HomeworkScheduleModalProps {
    isOpen: boolean;
    selectedItems: SelectedItem[];
    schedules: HomeworkSchedule[];
    onClose: () => void;
    onConfirm: (schedules: HomeworkSchedule[]) => void;
    bulkDays: boolean[];
    setBulkDays: React.Dispatch<React.SetStateAction<boolean[]>>;
    bulkInterval: number;
    setBulkInterval: React.Dispatch<React.SetStateAction<number>>;
}

const dayNames = ["일요일", "월요일", "화요일", "수요일", "목요일", "금요일", "토요일"];

const HomeworkScheduleModal = ({ isOpen, selectedItems, schedules, onClose, onConfirm, bulkDays, setBulkDays, bulkInterval, setBulkInterval }: HomeworkScheduleModalProps) => {
    const [showGuideModal, setShowGuideModal] = useState(false);
    const [guideMessage, setGuideMessage] = useState("");

    if (!isOpen) return null;

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const items = Array.from(schedules);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        const updatedSchedules = items.map((item, index) => ({
            ...item,
            order: index
        }));

        onConfirm(updatedSchedules);
    };

    const handleBulkDayChange = (dayIndex: number) => {
        setBulkDays(prev => {
            const newDays = [...prev];
            newDays[dayIndex] = !newDays[dayIndex];
            return newDays;
        });
    };

    const handleBulkApply = () => {
        const updatedSchedules = schedules.map(schedule => ({
            ...schedule,
            dayOfWeek: [...bulkDays],
            interval: bulkInterval
        }));
        onConfirm(updatedSchedules);
    };

    // 확인 버튼 클릭 시 검증 함수
    const handleConfirm = () => {
        const isValid = schedules.every(s => {
            // 날짜 지정 방식
            if (Array.isArray(s.dates) && s.dates.length > 0) {
                return s.dates.every(date => date !== "");
            }
            // 요일 지정 방식: dayOfWeek에 true가 하나 이상 있어야 함
            if (Array.isArray(s.dayOfWeek) && s.dayOfWeek.some(Boolean)) {
                return true;
            }
            return false;
        });

        if (!isValid) {
            setGuideMessage("모든 과제에 대해 요일 또는 날짜를 반드시 지정해야 합니다.");
            setShowGuideModal(true);
            setTimeout(() => setShowGuideModal(false), 3000);
            return;
        }
        onConfirm(schedules);
        onClose();
    };

    return (
        <>
            <Overlay>
                <ModalBox>
                    <Header>
                        <Title>과제 일정 관리</Title>
                        <CloseButton onClick={onClose}>×</CloseButton>
                    </Header>
                    <Content>
                        <InfoText>과제를 드래그 하시면 과제 순서를 변경하실 수 있습니다.</InfoText>
                        <BulkActionContainer>
                            <IntervalSection>
                                <IntervalLabel>주기</IntervalLabel>
                                <IntervalSelect value={bulkInterval} onChange={e => setBulkInterval(Number(e.target.value))}>
                                    <option value={1}>1주마다</option>
                                    <option value={2}>2주마다</option>
                                    <option value={3}>3주마다</option>
                                    <option value={4}>4주마다</option>
                                </IntervalSelect>
                            </IntervalSection>
                        </BulkActionContainer>
                        <BulkActionContainer>
                            <BulkActionLabel>과제 출제 요일 설정:</BulkActionLabel>
                            <DayCheckboxGroup>
                                {dayNames.map((name, idx) => (
                                    <label key={idx}>
                                        <input
                                            type="checkbox"
                                            checked={bulkDays[idx]}
                                            onChange={() => handleBulkDayChange(idx)}
                                        />
                                        {name}
                                    </label>
                                ))}
                            </DayCheckboxGroup>
                            <BulkApplyButton onClick={handleBulkApply} disabled={bulkDays.every(day => !day)}>
                                적용
                            </BulkApplyButton>
                        </BulkActionContainer>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="schedules">
                                {(provided) => (
                                    <ScheduleList
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                    >
                                        {schedules.map((schedule, index) => {
                                            const item = selectedItems.find(item => item.id === schedule.id);
                                            if (!item) return null;
                                            return (
                                                <Draggable
                                                    key={schedule.id}
                                                    draggableId={schedule.id}
                                                    index={index}
                                                >
                                                    {(provided) => (
                                                        <ScheduleItem
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                        >
                                                            <ItemName>{item.name}</ItemName>
                                                            <DayLabel>
                                                                {schedule.dayOfWeek.length === 0
                                                                    ? "-"
                                                                    : schedule.dayOfWeek
                                                                        .map((isSelected, idx) => isSelected ? dayNames[idx] : null)
                                                                        .filter(Boolean)
                                                                        .join(", ")}
                                                            </DayLabel>
                                                        </ScheduleItem>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </ScheduleList>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </Content>
                    <Footer>
                        <FooterButton onClick={onClose}>취소</FooterButton>
                        <FooterButtonPrimary onClick={handleConfirm}>확인</FooterButtonPrimary>
                    </Footer>
                </ModalBox>
            </Overlay>
            <Modal
                isOpen={showGuideModal}
                title="안내"
                content={guideMessage}
                onClose={() => setShowGuideModal(false)}
            />
        </>
    );
};

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
`;

const ModalBox = styled.div`
    background: white;
    min-width: 700px;
    max-width: 1200px;
    padding: 32px 32px 24px 32px;
    border-radius: 16px;
    box-shadow: 0 10px 25px rgba(0,0,0,0.12);
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
`;

const Title = styled.h2`
    font-size: 22px;
    font-weight: 700;
    color: #1E293B;
    margin: 0;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 28px;
    color: #A0AEC0;
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
        color: #FD0000;
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const InfoText = styled.div`
    color: #475569;
    font-size: 15px;
    margin-bottom: 8px;
    font-weight: 500;
`;

const BulkActionContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 24px;
    margin-bottom: 8px;
    padding: 20px 32px;
    background: #F8FAFC;
    border-radius: 10px;
    border: 1px solid #E2E8F0;
    flex-shrink: 0;
`;

const IntervalSection = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const IntervalLabel = styled.label`
    font-size: 16px;
    font-weight: 600;
    color: #2D3748;
`;

const IntervalSelect = styled.select`
    padding: 8px 16px;
    border: 1.5px solid #3182CE;
    border-radius: 8px;
    font-size: 15px;
    color: #1E293B;
    background: #F8FAFC;
    outline: none;
    transition: border 0.2s;
    &:focus {
        border: 2px solid #2563EB;
    }
`;

const BulkActionLabel = styled.span`
    font-size: 16px;
    font-weight: 600;
    color: #2D3748;
    white-space: nowrap;
`;

const DayCheckboxGroup = styled.div`
    display: flex;
    gap: 16px;
`;

const BulkApplyButton = styled.button`
    margin-left: 24px;
    padding: 10px 24px;
    background: #3182CE;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    cursor: pointer;
    &:disabled {
        background: #CBD5E0;
        cursor: not-allowed;
    }
`;

const ScheduleList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
    padding-right: 12px;
    max-height: 400px;
    &::-webkit-scrollbar {
        width: 10px;
    }
    &::-webkit-scrollbar-track {
        background: #F8FAFC;
        border-radius: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background: #CBD5E0;
        border-radius: 6px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: #A0AEC0;
    }
`;

const ScheduleItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 32px;
    background: #F8FAFC;
    border-radius: 10px;
    border: 1px solid #E2E8F0;
    flex-shrink: 0;
`;

const ItemName = styled.span`
    font-size: 15px;
    color: #2D3748;
`;

const DayLabel = styled.span`
    font-size: 15px;
    color: #4A5568;
    padding: 8px 20px;
    background: white;
    border-radius: 6px;
    border: 1px solid #E2E8F0;
    white-space: nowrap;
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
`;

const FooterButton = styled.button`
    padding: 10px 28px;
    background: white;
    color: #3182CE;
    border: 1.5px solid #3182CE;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    &:hover {
        background: #F0F8FF;
    }
`;

const FooterButtonPrimary = styled.button`
    padding: 10px 28px;
    background: #3182CE;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    &:hover {
        background: #2563EB;
    }
`;

export default HomeworkScheduleModal;